import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutGrid,
  PlusCircle,
  Wrench,
  BarChart3,
  ShieldCheck,
  ScanLine,
  ClipboardCheck,
  ClipboardList,
  Search,
  Bell,
  MapPin,
  Clock,
  ChevronDown,
  ChevronRight,
  Bike,
  ListOrdered,
  Check,
  Package,
  PackageOpen,
  Layers,
  Boxes,
  Receipt,
  FileBarChart,
  CarFront,
  ClipboardPlus,
  ListChecks,
  Menu,
  X,
  AlertTriangle,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { hasPerm } from "@/lib/permissions";
import { ROLE_LABELS, fmtDateTime } from "@/lib/format";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Custom sidebar icons rendered as <img/> (accept `className` for size).
const AV_ICON_URL = "https://static.prod-images.emergentagent.com/jobs/58aa5751-88e8-47a3-aa66-05c049d867f6/images/9c7562471d2a5cf9bc6aa3630ec5a45dbf591b726d360cd01a15973de88c9924.jpeg";
const PV_ICON_URL = "https://static.prod-images.emergentagent.com/jobs/58aa5751-88e8-47a3-aa66-05c049d867f6/images/ac7560d91cc5fd9e32cd03e7afac88a6a908d1966e18313e11b79218f0e1cfd8.jpeg";

const AvGroupIcon = ({ className = "" }) => (
  <img src={AV_ICON_URL} alt="Accidental Vehicles"
    className={`${className.replace(/text-\S+/g, "")} object-contain`}
    style={{ width: "18px", height: "18px" }} />
);
const PvGroupIcon = ({ className = "" }) => (
  <img src={PV_ICON_URL} alt="Pending Vehicles"
    className={`${className.replace(/text-\S+/g, "")} object-contain`}
    style={{ width: "18px", height: "18px" }} />
);

const NAV_DASHBOARD = {
  to: "/",
  label: "Spare Dashboard",
  icon: LayoutGrid,
  testid: "nav-dashboard",
};

// Top-level expandable parent modules. Accordion behaviour: only one parent
// can be open at any time. Each parent belongs to a section (the small grey
// uppercase heading rendered above it).
const PARENT_MODULES = [
  {
    key: "spares",
    label: "Spares",
    icon: Boxes,
    testid: "nav-group-spares",
    items: [
      { to: "/create", label: "Create Parts Request", icon: PlusCircle, testid: "nav-create", perm: "add" },
      { to: "/workbench", label: "Parts Workbench", icon: Wrench, testid: "nav-workbench" },
      { to: "/packing-slip/new", label: "New Packing Slip", icon: PackageOpen, testid: "nav-new-packing-slip", perm: "add" },
      { to: "/stock-planning", label: "Stock Planning", icon: Layers, testid: "nav-stock-planning" },
    ],
  },
  {
    key: "reports",
    label: "Reports",
    icon: FileBarChart,
    testid: "nav-group-reports",
    items: [
      { to: "/orders", label: "All Orders", icon: ListOrdered, testid: "nav-all-orders" },
      { to: "/reports", label: "Parts Reports", icon: BarChart3, testid: "nav-reports" },
      { to: "/packing-slips", label: "Saved Packing Slips", icon: Package, testid: "nav-saved-packing-slips" },
    ],
  },
  {
    key: "av",
    label: "Accidental Vehicles",
    icon: AvGroupIcon,
    testid: "nav-group-av",
    items: [
      { to: "/av/dashboard", label: "AV Dashboard", icon: LayoutGrid, testid: "nav-av-dashboard" },
      { to: "/av/new", label: "New AV Entry", icon: ClipboardPlus, testid: "nav-av-new", perm: "add" },
      { to: "/av/workbench", label: "AV Workbench", icon: ListChecks, testid: "nav-av-workbench" },
      { to: "/av/reports", label: "AV Reports", icon: FileBarChart, testid: "nav-av-reports" },
    ],
  },
  {
    key: "pv",
    label: "Pending Vehicles",
    icon: PvGroupIcon,
    testid: "nav-group-pv",
    // Per-item role gating: CRM sees Dashboard + Workbench only (no Upload).
    // Service Advisor / Workshop Manager / Service Manager / Admin see all.
    items: [
      { to: "/pv/dashboard", label: "PV Dashboard", icon: LayoutGrid, testid: "nav-pv-dashboard",
        allowed: ["admin", "service_advisor", "workshop_manager", "service_manager", "crm"] },
      { to: "/pv/workbench", label: "PV Workbench", icon: ListChecks, testid: "nav-pv-workbench",
        allowed: ["admin", "service_advisor", "workshop_manager", "service_manager", "crm"] },
      { to: "/pv/upload",    label: "PV Upload",    icon: PackageOpen, testid: "nav-pv-upload", perm: "add",
        allowed: ["admin", "service_advisor", "workshop_manager", "service_manager"] },
      { to: "/pv/reports",   label: "PV Reports",   icon: BarChart3, testid: "nav-pv-reports",
        allowed: ["admin", "service_advisor", "workshop_manager", "service_manager", "crm"] },
    ],
    // A user must be in this list to see the PV parent at all.
    allowedRoles: ["admin", "service_advisor", "workshop_manager", "service_manager", "crm"],
  },
  {
    key: "loc",
    label: "LOC Tracker",
    icon: AlertTriangle,
    testid: "nav-group-loc",
    items: [
      { to: "/loc", label: "LOC Tracker", icon: AlertTriangle, testid: "nav-loc-tracker" },
    ],
    allowedRoles: ["admin", "service_advisor", "workshop_manager", "service_manager", "floor_supervisor", "works_manager", "crm"],
  },
  {
    key: "masters",
    label: "Masters",
    icon: ClipboardPlus,
    testid: "nav-group-masters",
    items: [
      { to: "/masters/pv-status", label: "PV Status Master", icon: ListChecks, testid: "nav-master-pv-status",
        allowed: ["admin", "workshop_manager", "service_manager"] },
    ],
    allowedRoles: ["admin", "workshop_manager", "service_manager"],
  },
  {
    key: "security",
    label: "Security",
    icon: ShieldCheck,
    testid: "nav-group-security",
    items: [
      { to: "/security/scan",      label: "Scan Dispatch",      icon: ScanLine,       testid: "nav-security-scan",
        allowed: ["admin", "security"] },
      { to: "/security/awaiting",  label: "Awaiting Dispatch",  icon: ClipboardList,  testid: "nav-security-awaiting",
        allowed: ["admin", "security"] },
      { to: "/security/history",   label: "Dispatch History",   icon: ClipboardCheck, testid: "nav-security-history",
        allowed: ["admin", "security"] },
    ],
    allowedRoles: ["admin", "security"],
  },
];

// Section headings group the parents visually. Each section lists the parent
// keys (in display order) that sit under it.
const NAV_SECTIONS = [
  { label: "Spares", icon: Package, testid: "nav-section-spares", parents: ["spares", "reports"] },
  { label: "Service", icon: CarFront, testid: "nav-section-service", parents: ["av", "pv", "loc"] },
  { label: "Configuration", icon: Package, testid: "nav-section-config", parents: ["masters", "security"] },
];

const NAV_ADMIN = {
  to: "/admin",
  label: "Admin",
  icon: ShieldCheck,
  testid: "nav-admin",
};

// Helper: does a parent contain the currently-active route?
const isParentMatchingPath = (parent, path) =>
  parent.items.some((it) => path === it.to || path.startsWith(it.to + "/"));

const Layout = () => {
  const {
    currentUser,
    locations, currentLocation, switchLocation,
    signOut, inactiveLogout, clearInactiveLogout,
  } = useApp();
  const [now, setNow] = useState(new Date());
  const navigate = useNavigate();
  const routeLoc = useLocation();

  // Accordion behaviour: at most one parent module is expanded at a time.
  // `openParent` holds the key of the currently expanded parent, or null.
  // The initial parent is whichever one matches the active route (so the
  // active child is visible immediately on load).
  const [openParent, setOpenParent] = useState(() => {
    const match = PARENT_MODULES.find((p) => isParentMatchingPath(p, routeLoc.pathname));
    return match ? match.key : null;
  });

  // Mobile drawer state. The sidebar is permanently visible on lg+, but on
  // smaller screens it slides in from the left as an overlay drawer.
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  // When the route changes, auto-expand the parent that owns the active
  // child so the user can always see context — and keep accordion-style
  // (siblings stay collapsed).
  React.useEffect(() => {
    const match = PARENT_MODULES.find((p) => isParentMatchingPath(p, routeLoc.pathname));
    if (match) setOpenParent(match.key);
  }, [routeLoc.pathname]);

  // Toggling a parent: clicking the already-open parent collapses it;
  // clicking a different parent replaces the open one (siblings collapse).
  const toggleParent = (key) =>
    setOpenParent((prev) => (prev === key ? null : key));

  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  // If the signed-in user has been deactivated mid-session, surface the
  // exact required message and send them back to /login.
  if (inactiveLogout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#faf8ff] p-6" data-testid="inactive-gate">
        <div className="bg-white border border-[#e2e1eb] shadow-sm rounded-xl max-w-md w-full p-8 text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h2 className="text-[20px] font-bold text-[#1a1b22]">Account Inactive</h2>
          <p className="text-[13px] text-[#444653]" data-testid="inactive-message">
            Your account is inactive. Please contact administrator.
          </p>
          <button
            data-testid="inactive-back-to-login"
            onClick={() => { clearInactiveLogout(); navigate("/login", { replace: true }); }}
            className="mt-2 px-4 py-2 rounded-md bg-yamaha-700 hover:bg-yamaha-800 text-white text-[12.5px] font-semibold"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center text-yamaha-700 font-medium">
        Loading IVA Motors Service Sync...
      </div>
    );
  }

  const initials = currentUser.name
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  // Multi-select location helpers.
  // `currentLocation` is "ALL" or a comma-separated list of codes (e.g. "JEY,RAY").
  const allowedLocations = locations.filter(
    (l) => currentUser.role === "admin" || currentUser.location_codes.includes(l.code)
  );
  const selectedCodes =
    currentLocation === "ALL"
      ? allowedLocations.map((l) => l.code)
      : (currentLocation || "")
          .split(",")
          .map((c) => c.trim())
          .filter((c) => c && allowedLocations.find((l) => l.code === c));
  const isAllSelected =
    allowedLocations.length > 0 && selectedCodes.length === allowedLocations.length;

  // Header display: chip with up to 3 location names, then "+N more".
  const locationDisplay = (() => {
    if (isAllSelected || selectedCodes.length === 0) return "All Locations";
    const names = selectedCodes.map(
      (c) => locations.find((l) => l.code === c)?.name || c
    );
    if (names.length <= 3) return names.join(" + ");
    return `${names.slice(0, 3).join(" + ")} +${names.length - 3} more`;
  })();

  const setLocationSelection = (codes) => {
    if (!codes || codes.length === 0) {
      // Empty selection collapses back to "ALL" — we never show zero locations.
      switchLocation("ALL");
    } else if (codes.length === allowedLocations.length) {
      switchLocation("ALL");
    } else {
      switchLocation(codes.join(","));
    }
  };

  const toggleLocation = (code) => {
    // If we're currently on "ALL" and the user picks a specific location,
    // start with just that one — that's what users intuitively expect.
    if (currentLocation === "ALL" || isAllSelected) {
      setLocationSelection([code]);
      return;
    }
    if (selectedCodes.includes(code)) {
      setLocationSelection(selectedCodes.filter((c) => c !== code));
    } else {
      setLocationSelection([...selectedCodes, code]);
    }
  };

  // "All Locations" is a one-way convenience action — clicking it always
  // selects everything. To narrow down, the user uncheckes individual rows.
  const toggleAllLocations = () => {
    setLocationSelection(allowedLocations.map((l) => l.code));
  };

  return (
    <div className="min-h-screen flex bg-[#faf8ff] font-sans text-[#1a1b22]">
      {/* Mobile backdrop — only rendered when the drawer is open on small screens. */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={closeMobile}
          data-testid="sidebar-backdrop"
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
        />
      )}

      {/* SIDEBAR */}
      <aside
        className={`w-[240px] bg-white border-r border-[#e2e1eb] flex flex-col fixed top-0 bottom-0 left-0 z-40 transition-transform duration-200 lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="h-16 px-5 flex items-center gap-3 border-b border-[#e2e1eb]">
          <img
            src="/logo.png"
            alt="Service Sync"
            className="w-9 h-9 rounded-md object-cover"
            data-testid="brand-logo"
          />
          <div className="flex-1">
            <div className="text-[15px] font-semibold text-yamaha-700 leading-tight">IVA Motors</div>
            <div className="text-[11px] tracking-wider uppercase text-[#747684]">Service Sync</div>
          </div>
          <button
            type="button"
            onClick={closeMobile}
            aria-label="Close menu"
            data-testid="sidebar-close"
            className="lg:hidden p-1.5 rounded-md hover:bg-[#f4f3fc] text-[#444653]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {/* Dashboard — top-level (gated by module permission "dashboard.view") */}
          {hasPerm(currentUser, "dashboard", "view") && (
          <NavLink
            to={NAV_DASHBOARD.to}
            end
            onClick={closeMobile}
            data-testid={NAV_DASHBOARD.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] transition-colors ${
                isActive
                  ? "bg-yamaha-50 text-yamaha-700 font-semibold"
                  : "text-[#444653] hover:bg-[#f4f3fc]"
              }`
            }
          >
            <NAV_DASHBOARD.icon className="w-5 h-5" />
            <span>{NAV_DASHBOARD.label}</span>
          </NavLink>
          )}

          {NAV_SECTIONS.map((section) => {
            // Pre-compute which parents are actually visible so we can skip the whole section (header + spacing) when nothing remains.
            const visibleParents = section.parents
              .map((k) => PARENT_MODULES.find((p) => p.key === k))
              .filter((parent) => {
                if (!parent) return false;
                if (!hasPerm(currentUser, parent.key, "view")) return false;
                if (parent.allowedRoles && currentUser?.role !== "admin" &&
                    !parent.allowedRoles.includes(currentUser?.role)) return false;
                const visibleItems = parent.items.filter((it) => {
                  if (it.allowed && currentUser?.role !== "admin"
                      && !it.allowed.includes(currentUser?.role)) return false;
                  if (it.perm && !hasPerm(currentUser, parent.key, it.perm)) return false;
                  return true;
                });
                return visibleItems.length > 0;
              });
            if (visibleParents.length === 0) return null;
            return (
            <React.Fragment key={section.label}>
              <div className="pt-4 pb-1 px-3" data-testid={section.testid}>
                <div className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[#9d9fae]">
                  <section.icon className="w-3.5 h-3.5" />
                  <span>{section.label}</span>
                </div>
              </div>

              {visibleParents.map((parent) => {
                // Per-item filter: role gating + module-action gating (e.g. "add" items hide when Add perm is off).
                const visibleItems = parent.items.filter((it) => {
                  if (it.allowed && currentUser?.role !== "admin"
                      && !it.allowed.includes(currentUser?.role)) return false;
                  if (it.perm && !hasPerm(currentUser, parent.key, it.perm)) return false;
                  return true;
                });
                const isOpen = openParent === parent.key;
                const parentActive = isParentMatchingPath(parent, routeLoc.pathname);
                const ParentIcon = parent.icon;
                return (
                  <div key={parent.key} className="space-y-0.5">
                    <button
                      type="button"
                      onClick={() => toggleParent(parent.key)}
                      data-testid={parent.testid}
                      aria-expanded={isOpen}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-semibold transition-colors ${
                        parentActive
                          ? "bg-yamaha-50 text-yamaha-700"
                          : "text-[#1a1b22] hover:bg-[#f4f3fc]"
                      }`}
                    >
                      <ParentIcon className="w-[18px] h-[18px] text-yamaha-700" />
                      <span className="flex-1 text-left">{parent.label}</span>
                      <ChevronRight
                        className={`w-3.5 h-3.5 text-[#747684] transition-transform ${
                          isOpen ? "rotate-90" : ""
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="pl-3 ml-3 border-l border-[#ece9f5] space-y-0.5">
                        {visibleItems.map((item) => (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            onClick={closeMobile}
                            data-testid={item.testid}
                            className={({ isActive }) =>
                              `flex items-center gap-2.5 pl-3 pr-3 py-2 rounded-lg text-[13px] transition-colors ${
                                isActive
                                  ? "bg-yamaha-50 text-yamaha-700 font-semibold"
                                  : "text-[#444653] hover:bg-[#f4f3fc]"
                              }`
                            }
                          >
                            <item.icon className="w-4 h-4" />
                            <span>{item.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
            );
          })}

          {/* Admin — visible only to admin role. Route also enforces the guard. */}
          {currentUser?.role === "admin" && (
            <div className="pt-4">
              <NavLink
                to={NAV_ADMIN.to}
                onClick={closeMobile}
                data-testid={NAV_ADMIN.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] transition-colors ${
                    isActive
                      ? "bg-yamaha-50 text-yamaha-700 font-semibold"
                      : "text-[#444653] hover:bg-[#f4f3fc]"
                  }`
                }
              >
                <NAV_ADMIN.icon className="w-5 h-5" />
                <span>{NAV_ADMIN.label}</span>
              </NavLink>
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-[#e2e1eb] text-[11px] text-[#747684]">
          v1.0 · {new Date().getFullYear()} IVA Motors
        </div>
      </aside>

      {/* MAIN */}
      <div className="flex-1 lg:ml-[240px] flex flex-col min-h-screen">
        {/* TOPBAR */}
        <header
          className="h-16 bg-white border-b border-[#e2e1eb] flex items-center px-4 lg:px-6 gap-3 lg:gap-4 sticky top-0 z-20"
          data-testid="topbar"
        >
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            data-testid="sidebar-toggle"
            className="lg:hidden p-2 -ml-2 rounded-md hover:bg-[#f4f3fc] text-[#444653]"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex-1 max-w-xl relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#747684]" />
            <input
              data-testid="global-search-input"
              placeholder="Search orders, customer, mobile, VIN, part, PO, invoice..."
              className="w-full pl-10 pr-4 py-2 rounded-full bg-[#f4f3fc] border border-transparent text-[13px] focus:outline-none focus:border-yamaha-300"
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                const term = e.target.value.trim();
                if (!term) return;
                // Stay on the current module when possible. All Orders is the
                // comprehensive view, so default any unknown route there too.
                const onWorkbench = routeLoc.pathname.startsWith("/workbench");
                const dest = onWorkbench ? "/workbench" : "/orders";
                navigate(`${dest}?q=${encodeURIComponent(term)}`);
              }}
            />
          </div>

          {/* Location switcher — multi-select */}
          <Popover>
            <PopoverTrigger
              data-testid="location-switcher"
              className="flex items-center gap-2 text-[13px] px-3 py-1.5 rounded-md hover:bg-[#f4f3fc] max-w-[420px]"
            >
              <MapPin className="w-4 h-4 text-yamaha-700 flex-shrink-0" />
              <span className="font-medium truncate" data-testid="location-switcher-label">
                {locationDisplay}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-[#747684] flex-shrink-0" />
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-64 p-0"
              data-testid="location-switcher-menu"
            >
              <div className="px-3 py-2 border-b border-[#e2e1eb] text-[12px] font-semibold text-[#747684] uppercase tracking-wider">
                Switch Location
              </div>
              <div className="max-h-72 overflow-y-auto py-1">
                {allowedLocations.length > 1 && (
                  <button
                    type="button"
                    onClick={toggleAllLocations}
                    data-testid="loc-all"
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] hover:bg-[#f4f3fc]"
                  >
                    <span
                      className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        isAllSelected
                          ? "bg-yamaha-700 border-yamaha-700"
                          : "border-[#cfcdd8]"
                      }`}
                    >
                      {isAllSelected && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span className="font-semibold text-[#1a1b22]">All Locations</span>
                  </button>
                )}
                {allowedLocations.map((l) => {
                  const checked = selectedCodes.includes(l.code);
                  return (
                    <button
                      type="button"
                      key={l.code}
                      onClick={() => toggleLocation(l.code)}
                      data-testid={`loc-${l.code}`}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] hover:bg-[#f4f3fc]"
                    >
                      <span
                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                          checked
                            ? "bg-yamaha-700 border-yamaha-700"
                            : "border-[#cfcdd8]"
                        }`}
                      >
                        {checked && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <span className="text-[#1a1b22]">{l.name}</span>
                      <span className="ml-auto text-[11px] text-[#747684] font-mono">{l.code}</span>
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>

          <div className="hidden md:flex items-center gap-2 text-[12px] text-[#747684]">
            <Clock className="w-4 h-4" />
            <span>{fmtDateTime(now.toISOString())} IST</span>
          </div>

          <button className="relative p-2 rounded-full hover:bg-[#f4f3fc]" data-testid="notifications-btn">
            <Bell className="w-5 h-5 text-[#444653]" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
          </button>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="user-menu"
              className="flex items-center gap-3 pl-2 pr-3 py-1 rounded-full hover:bg-[#f4f3fc]"
            >
              <Avatar className="w-9 h-9 bg-yamaha-700 text-white">
                <AvatarFallback className="bg-yamaha-700 text-white text-[13px] font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="text-left leading-tight">
                <div className="text-[13px] font-semibold">{currentUser.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#747684]">
                  {ROLE_LABELS[currentUser.role]}
                </div>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-[#747684]" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel className="leading-tight">
                <div className="text-[13px] font-semibold">{currentUser.name}</div>
                <div className="text-[10.5px] text-[#747684] mt-0.5">
                  {currentUser.mobile ? `+91 ${currentUser.mobile}` : currentUser.email || "—"}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#747684] mt-0.5">
                  {ROLE_LABELS[currentUser.role]}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="menu-change-password"
                onClick={() => navigate("/account/password")}
              >
                Change Password
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="menu-logout"
                onClick={async () => {
                  await signOut();
                  navigate("/login", { replace: true });
                }}
                className="text-rose-600 focus:text-rose-600"
              >
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
